import { ExportResponse, NotificationService } from '@aston/foundation';
import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { switchMap, take } from 'rxjs/operators';
import { saveAs } from 'file-saver';
import { Store } from '@ngrx/store';

import { homeKpiParamsToHttpParams, mergeHttpParams, portfolioKpiParamsToHttpParams } from '../../shared-module/functions';
import { HomeDashboardStore } from '../../home-module/stores/home-dashboard/home-dashboard.store';
import { selectConfig } from '../../root-store/app-store/selectors';
import { HomeScopeType } from '../../home-module/enums';

// Maps the trigger-export AI tool's catalog ids (Core.Middle.Services/Ai/TriggerExportAiToolHandler.cs)
// to the ChartsController export route segment, mirroring HomeExportsService's routes.
const EXPORT_ROUTES: Record<string, string> = {
	ageingBalance: 'AgeingBalance',
	ageingBalanceDetailed: 'AgeingBalanceDetailed',
	dsoMonitoring: 'Dso',
	averageOverdue: 'AverageOverdue',
	averageDisputeResolution: 'AverageDisputeResolution',
	topOutstandingClients: 'ClientTop/Outstanding',
	topDebtorsWithClaims: 'ClientTop/Litigation',
	estimatedCashing: 'EstimatedCashing',
	outstandingDunningLevel: 'OutstandingDunningLevel',
	totalRevenue: 'TotalRevenue',
}

@Injectable({ providedIn: 'root' })
export class AiExportTriggerService {
	private http = inject(HttpClient)
	private store = inject(Store)
	private homeDashboardStore = inject(HomeDashboardStore)
	private notify = inject(NotificationService)

	trigger(exportId: string, encodedFilters: string) {
		const route = EXPORT_ROUTES[exportId]
		if (!route) return

		const scope = this.homeDashboardStore.scope()
		const isPortfolio = !scope.is(HomeScopeType.Users)
		const scopeSegment = isPortfolio ? 'Portfolio' : 'User'
		const scopeParams = isPortfolio
			? portfolioKpiParamsToHttpParams(scope.getParams())
			: homeKpiParamsToHttpParams(scope.getParams())

		const sieveFilters = encodedFilters ? this.decodeBase64Url(encodedFilters) : ''
		const params = sieveFilters
			? mergeHttpParams(scopeParams, new HttpParams().set('filters', sieveFilters))
			: scopeParams

		this.store.select(selectConfig).pipe(
			take(1),
			switchMap(config => this.http.get(`${config.apiUrl}/api/Charts/${scopeSegment}/${route}/Export?${params}`, { observe: 'response', responseType: 'blob' })),
		).subscribe({
			next: response => {
				const exportResponse = new ExportResponse(response)
				saveAs(exportResponse.blob, exportResponse.fileName)
			},
			error: () => this.notify.errorMessage('Errors.DefaultClient'),
		})
	}

	private decodeBase64Url(value: string): string {
		const base64 = value.replace(/-/g, '+').replace(/_/g, '/')
		const padded = base64 + '='.repeat((4 - base64.length % 4) % 4)
		return atob(padded)
	}
}
